import { DeleteForever } from "@mui/icons-material"
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material"
import api from "api/client";
import { useSnackbar } from "providers/SnackbarProvider";
import { memo, useState } from "react"
import { useNavigate } from "react-router-dom";

const CallOffInterviewDialog = ({
    openDeleteDialog,
    setOpenDeleteDialog,
    title = "Call Off Interview",
    desc = "Are you sure you want to cancel the interview?",
    btnTitle = "Call Off Interview",
    interviewId
}) => {
    const navigate = useNavigate()
    const { showSnackbar } = useSnackbar();

    const [reason, setReason] = useState("")
    const [loading, setLoading] = useState(false)

    const handleReasonChange = (e) => {
        const value = e.target.value
        setReason(value)
    }

    const handleDeleteInterview = async () => {
        try {
            if (!reason) {
                showSnackbar("error", "Please enter the reason before submitting")
                return;
            }
            setLoading(true);

            const payload = {
                interview_id: interviewId,
                response: "REJECTED",
                rejection_reason: reason
            }
            const response = await api.post("RespondToInterviewInvitation", payload)

            if (response?.data?.status === "success") {
                showSnackbar("success", response?.data?.message || "Successfully updated the status")
                setOpenDeleteDialog(false)
                setReason("")
                navigate("/interview-schedule-list")
            } else {
                showSnackbar("error", "Unable to update the status")
            }
        } catch (err) {
            console.error("Error", err)
            showSnackbar("error", "Failed to update the status")
        } finally {
            setLoading(false);
        }
    }

    return (
        <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    fontSize: "18px",
                    fontWeight: 600,
                    pb: 1,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        backgroundColor: "#FFF0F0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <DeleteForever
                        sx={{
                            color: "#FF5C5C",
                            fontSize: 24,
                        }}
                    />
                </Box>

                {title}
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <Typography
                    sx={{
                        fontSize: "14px",
                        color: "#666",
                        lineHeight: 1.6,
                    }}
                >
                    {desc}
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Reason
                        <span style={{ color: "red" }}>
                            *
                        </span>
                    </Typography>
                    <TextField
                        value={reason}
                        onChange={handleReasonChange}
                        multiline
                        fullWidth
                        placeholder="Enter your reason"
                        minRows={2}
                        maxRows={3}
                        sx={{
                            "&. MuiInputBase-input": {
                                fontSize: '12px'
                            },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                            },

                            "& .MuiInputBase-input": {
                                fontSize: "14px",
                            },

                            "& .MuiInputBase-input::placeholder": {
                                fontSize: "14px",
                                opacity: 1,
                                color: '#a2a2a2'
                            },
                            mt: 0.5
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 2,
                    pb: 2,
                    gap: 1,
                }}
            >
                <Button
                    onClick={() => setOpenDeleteDialog(false)}
                    variant="outlined"
                    disabled={loading}
                    sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        px: 2.5,
                        color: "#555",
                        borderColor: "#DADADA",
                        "&:hover": {
                            borderColor: "#BDBDBD",
                            backgroundColor: "#F8F8F8",
                        },
                    }}
                >
                    Close
                </Button>

                <Button
                    onClick={handleDeleteInterview}
                    variant="contained"
                    startIcon={
                        loading ? (
                            <CircularProgress
                                size={18}
                                sx={{ color: "#a2a2a2" }}
                            />
                        ) : (
                            <DeleteForever sx={{ fontSize: 19 }} />
                        )
                    }
                    sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        px: 2.5,
                        backgroundColor: "#FF5C5C",
                        boxShadow: "none",
                        "&:hover": {
                            backgroundColor: "#E94B4B",
                            boxShadow: "none",
                        },
                    }}
                    disabled={loading}
                >
                    {loading ? "Cancelling interview..." : btnTitle}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default memo(CallOffInterviewDialog)